import { computed, inject } from '@angular/core';
import { patchState, signalStore, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';

import {
  setFulfilled,
  setPending,
  setError,
  withRequestStatus,
  withSyncToWebStorage,
} from '@shared/stores/store-features';
import { CheckoutService } from './checkout.service';
import { Booking } from '@shared/models/booking';
import { BookingRequest } from '@shared/models/bookingRequest';
import { CheckoutBookingPeriod } from './models/checkout-booking-period.model';
import { BasketItem } from '../associations/components/basket/basket';
import { Observable, catchError, firstValueFrom, forkJoin, of } from 'rxjs';
import { BookingStatus } from '../booking/calender/model/enums';
import { BookingTypeName } from '@shared/models/enums';
import { SearchQueryParams } from '../associations/models/search-response';

/** En bookingperiode API'et afviste, med begrundelsen fra svaret. */
export interface AddBookingPeriodFailure {
  addBookingPeriodError?: string;
}

/**
 * Begrundelsen for en afvist bookingperiode: ProblemDetails' `detail` (som API'et bruger til fx
 * maksimumtiden), ellers den første streng i et fejl-array, ellers ingenting brugbart.
 */
function addBookingPeriodReason(error: unknown): string {
  // The HttpErrorResponse body: ProblemDetails, or a plain array of messages.
  const body = (error as { error?: { detail?: string } | string[] } | null)?.error;

  if (body && !Array.isArray(body) && body.detail) {
    return body.detail;
  }

  if (Array.isArray(body) && body.length > 0) {
    return body[0];
  }

  return 'Perioden kunne ikke oprettes';
}

/**
 * Keep an eye on this repo for extensions
 * @ngrx-traits/signals
 */

interface CheckoutState {
  bookingId: number;
  booking: Booking;
  bookingRequest: BookingRequest;
  bookingType: BookingTypeName;
  lastSearchParams: SearchQueryParams | null;
}

export const CheckoutStore = signalStore(
  { providedIn: 'root' },

  signalStoreFeature(
    withState<CheckoutState>({
      bookingId: null,
      booking: null,
      bookingRequest: null,
      bookingType: BookingTypeName.UNION,
      lastSearchParams: null,
    }),
  ),
  withRequestStatus(),

  withSyncToWebStorage({
    key: 'abook-checkout-store',
    type: 'session',
    restoreOnInit: true,
    saveStateChangesAfterMs: 300,
    // optionally, filter the state before saving to the storage
    // filterState: ({ orderItemsEntityMap, orderItemsIds }) => ({
    //   orderItemsEntityMap,
    //   orderItemsIds,
    // }),
  }),

  withComputed(({ booking }) => ({
    itemsCount: computed(() => booking()?.bookingPeriods.length),
    // calucate total price of all items in basket
    total: computed(() => {
      let totalPrice = 0;

      for (const bp of booking()?.bookingPeriods || []) {
        totalPrice += bp.price;
      }
      return totalPrice;
    }),
  })),

  withMethods((store) => {
    const checkoutService = inject(CheckoutService);

    return {
      setBookingId(id: number) {
        patchState(store, { bookingId: id });
      },

      setBookingType(bookingType: BookingTypeName) {
        // Sync journey with booking type (no casting needed - they use the same enum)

        patchState(store, { bookingType: bookingType });
      },

      setLastSearchParams(params: SearchQueryParams) {
        patchState(store, { lastSearchParams: params });
      },

      setRequestFulfilled() {
        patchState(store, setFulfilled());
      },

      setRequestPending() {
        patchState(store, setPending());
      },

      async updateBooking(id: number, bookingRequest: BookingRequest): Promise<void> {
        patchState(store, { bookingRequest: bookingRequest }, setPending());

        const booking = await firstValueFrom(checkoutService.updateBooking(id, bookingRequest));
        patchState(store, { booking: booking }, setFulfilled());
      },
      // PROMISE based API EXAMPLE
      async getBooking(id: number): Promise<void> {
        patchState(store, setPending());
        const booking = await firstValueFrom(checkoutService.getBooking(id));
        patchState(store, { booking: booking }, setFulfilled());
      },
      async requestBooking(bookingRequest: BookingRequest): Promise<void> {
        patchState(store, setPending());
        await firstValueFrom(checkoutService.createBooking(bookingRequest));
        patchState(store, { bookingRequest: bookingRequest }, setFulfilled());
      },
      async completeBooking(bookingId: number): Promise<void> {
        patchState(store, setPending());
        try {
          await firstValueFrom(checkoutService.completeBooking(bookingId));
          const refreshed = await firstValueFrom(checkoutService.getBooking(bookingId));
          patchState(store, { booking: refreshed }, setFulfilled());
        } catch (error) {
          console.error('checkoutStore - Error completing booking:', error);
          patchState(store, setError(error));
        } finally {
          patchState(store, { bookingId: null, bookingRequest: null });
        }
      },

      clearCheckoutContext(): void {
        patchState(store, { bookingId: null, bookingRequest: null });
      },

      async getBookingByUid(uid: string): Promise<void> {
        try {
          const booking = await firstValueFrom(checkoutService.getBookingByUid(uid));
          patchState(store, { booking });
        } catch {
          // non-fatal: complete page falls back to whatever is in the store
        }
      },

      clearBooking(bookingId: number): Observable<unknown> {
        // clear all state in store
        patchState(store, { bookingId: null, booking: null, bookingRequest: null });
        return checkoutService.deleteBooking(bookingId);
      },

      /**
       * API on basket checkout
       * @param bookingTypeId The periods' booking type when it is not the journey's: an organisation
       * (company, internal) books `Booking`, not the association types Foreningsbooking/Kamp (#950).
       */
      addBookingPeriods(
        bookingId: number,
        basketItems: BasketItem[],
        associationName: string = null,
        bookingTypeId: BookingTypeName = store.bookingType(),
      ): Observable<unknown[]> {

        const requests = basketItems.map((basketItem: BasketItem) => {
          const checkoutBookingPeriod: CheckoutBookingPeriod = {
            bookingId: bookingId,
            resourceId: basketItem.resourceId,
            bookingTypeId: bookingTypeId,
            note: associationName ? associationName : null,
            price: basketItem.price,
            statusId: BookingStatus.New,
            start: basketItem.start,
            end: basketItem.end,
            activityId: basketItem.activityId,
          };

          return checkoutService.addBookingPeriod(checkoutBookingPeriod).pipe(
            catchError((error) => {
              console.error('Error adding item to booking', error);

              // Fejlen fanges pr. periode, så de øvrige stadig oprettes — men den skal kunne
              // genkendes af den, der kaldte, og bære API'ets egen begrundelse (fx maksimumtiden).
              // Ellers fortsætter flowet som om intet var afvist.
              return of<AddBookingPeriodFailure>({ addBookingPeriodError: addBookingPeriodReason(error) });
            }),
          );
        });

        return forkJoin(requests); // Execute all HTTP requests in parallel
      },
    };
  }),
);
